/**
 * 项目配置管理系统
 * 配置数据与管理逻辑分离，支持多项目配置场景
 */

// 导入项目配置数据
import { projectConfigs, ProjectConfigError } from './projectConfigs.js'

/**
 * 获取项目标识
 * 从命令行参数或环境变量中获取（不设置默认值）
 * 支持的命令行参数格式：
 *   1. npm run serve -- --project kyzq
 *   2. npm run serve -- --kyzq (直接使用参数名作为项目标识)
 *   3. npm run serve -- --project=kyzq
 * 
 * 注意：vue.config.js 中已通过 webpack.DefinePlugin 将参数注入到 process.argv.project
 */
export const getProjectId = () => {
  try {
    // 优先级1: 从全局变获取（由 vue.config.js 的 DefinePlugin 注入）
    console.log(window.project)
    
    if (window.project) {
      const projectId = project
      return projectId
    }
    
    // 不设置默认项目标识，必须显式配置
    console.warn('[ProjectConfig] 未获取到项目标识，请通过命令行参数(--project kyzq 或 --kyzq)或环境变量(VUE_APP_PROJECT_ID)配置')
    return ''
  } catch (e) {
    console.error('[ProjectConfig] 获取项目标识失败:', e)
    return ''
  }
}

/**
 * 根据项目标识获取项目配置
 * @param {string} projectId 项目标识
 * @returns {object|null} 项目配置对象或null
 */
export const getProjectConfig = (projectId) => {
  // 参数验证
  if (!projectId || typeof projectId !== 'string' || projectId.trim() === '') {
    console.warn('[ProjectConfig] 项目标识无效:', projectId)
    return null
  }
  
  const trimmedId = projectId.trim()
  
  // 使用数组find方法查找配置（高效查找）
  const config = projectConfigs.find(item => item.projectId === trimmedId)
  
  if (!config) {
    console.warn(`[ProjectConfig] 未找到项目标识为 "${trimmedId}" 的配置`)
    return null
  }
  
  console.log(`[ProjectConfig] 找到项目配置: ${trimmedId} - ${config.projectName}`)
  return config
}

/**
 * 获取当前项目的完整配置信息
 * @returns {object} 包含配置信息和验证状态的对象
 */
export const getCurrentProjectConfig = () => {
  // 1. 获取项目标识
  const projectId = getProjectId()
  
  if (!projectId) {
    console.warn('[ProjectConfig] 未获取到有效的项目标识')
    return {
      valid: false,
      reason: ProjectConfigError.NO_PROJECT_ID,
      projectId: '',
      config: null,
      errorCode: 'NO_PROJECT_ID'
    }
  }
  
  // 2. 根据项目标识获取配置
  const config = getProjectConfig(projectId)
  
  if (!config) {
    console.warn(`[ProjectConfig] 根据项目标识 "${projectId}" 未查询到对应的项目配置`)
    return {
      valid: false,
      reason: ProjectConfigError.CONFIG_NOT_FOUND,
      projectId,
      config: null,
      errorCode: 'CONFIG_NOT_FOUND'
    }
  }
  
  // 3. 验证项目状态
  if (config.status !== 'active') {
    console.info(`[ProjectConfig] 项目 "${projectId}" 当前状态为 "${config.status}"，未激活`)
    return {
      valid: false,
      reason: ProjectConfigError.PROJECT_INACTIVE,
      projectId,
      config,
      shareEnabled: false,
      errorCode: 'PROJECT_INACTIVE'
    }
  }
  
  // 4. 验证分享展示标识
  const shareEnabled = config.wechat?.shareEnabled ?? config.features?.shareEnabled ?? false
  
  if (!shareEnabled) {
    console.info('[ProjectConfig] 项目配置中的分享展示标识设置为false')
    return {
      valid: false,
      reason: ProjectConfigError.SHARE_DISABLED,
      projectId,
      config,
      shareEnabled: false,
      errorCode: 'SHARE_DISABLED'
    }
  }
  
  // 5. 验证微信appid参数
  const appid = config.wechat?.appid
  
  if (!appid || appid.trim() === '') {
    console.warn('[ProjectConfig] 项目配置中未正确配置微信appid参数')
    return {
      valid: false,
      reason: ProjectConfigError.APPID_MISSING,
      projectId,
      config,
      shareEnabled: true,
      appidValid: false,
      errorCode: 'APPID_MISSING'
    }
  }
  
  // 6. 所有条件都满足
  console.log('[ProjectConfig] 分享功能验证通过')
  return {
    valid: true,
    reason: '配置验证通过',
    projectId,
    config,
    shareEnabled: true,
    appidValid: true,
    appid,
    errorCode: null
  }
}

/**
 * 获取所有项目配置列表
 * @returns {array} 所有项目配置的数组
 */
export const getAllProjectConfigs = () => {
  console.log(`[ProjectConfig] 获取所有项目配置，共 ${projectConfigs.length} 个项目`)
  return [...projectConfigs] // 返回副本，防止直接修改原数组
}

/**
 * 获取所有活跃项目配置列表
 * @returns {array} 所有活跃项目配置的数组
 */
export const getActiveProjectConfigs = () => {
  const activeConfigs = projectConfigs.filter(item => item.status === 'active')
  console.log(`[ProjectConfig] 获取活跃项目配置，共 ${activeConfigs.length} 个项目`)
  return activeConfigs
}

/**
 * 检查项目标识是否存在
 * @param {string} projectId 项目标识
 * @returns {boolean} 是否存在
 */
export const hasProjectConfig = (projectId) => {
  if (!projectId || typeof projectId !== 'string') {
    return false
  }
  return projectConfigs.some(item => item.projectId === projectId.trim())
}

/**
 * 获取项目配置数量
 * @returns {number} 项目配置数量
 */
export const getProjectConfigCount = () => {
  return projectConfigs.length
}

/**
 * 获取项目配置摘要信息
 * @returns {array} 包含项目标识和名称的摘要数组
 */
export const getProjectConfigSummaries = () => {
  return projectConfigs.map(item => ({
    projectId: item.projectId,
    projectName: item.projectName,
    status: item.status,
    shareEnabled: item.wechat?.shareEnabled ?? item.features?.shareEnabled ?? false
  }))
}

/**
 * 批量获取多个项目配置
 * @param {array} projectIds 项目标识数组
 * @returns {array} 匹配的项目配置数组
 */
export const getProjectConfigsByIds = (projectIds) => {
  // 参数验证
  if (!projectIds || !Array.isArray(projectIds)) {
    console.warn('[ProjectConfig] 项目标识数组无效')
    return []
  }
  
  // 过滤无效标识
  const validIds = projectIds.filter(id => id && typeof id === 'string')
  
  // 使用Set提高查找效率
  const idSet = new Set(validIds.map(id => id.trim()))
  
  // 批量查找
  const results = projectConfigs.filter(item => idSet.has(item.projectId))
  
  console.log(`[ProjectConfig] 批量获取项目配置，请求 ${projectIds.length} 个，找到 ${results.length} 个`)
  return results
}

/**
 * 检查分享功能是否可以展示
 * @returns {boolean} 是否可以展示
 */
export const canShowShare = () => {
  const result = getCurrentProjectConfig()
  return result.valid
}

/**
 * 获取分享功能展示的详细原因
 * @returns {object} 验证结果详情
 */
export const getShareVisibilityDetails = () => {
  return getCurrentProjectConfig()
}

/**
 * 验证项目配置的完整性
 * @param {object} config 项目配置对象
 * @returns {object} 验证结果
 */
export const validateProjectConfig = (config) => {
  const errors = []
  const warnings = []
  
  // 检查必需字段
  if (!config) {
    errors.push('配置对象为空')
    return { valid: false, errors, warnings }
  }
  
  if (!config.projectId) {
    errors.push('缺少项目标识(projectId)')
  }
  
  if (!config.projectName) {
    warnings.push('缺少项目名称(projectName)')
  }
  
  // 检查微信配置
  if (!config.wechat) {
    errors.push('缺少微信配置(wechat)')
  } else {
    if (!config.wechat.appid) {
      warnings.push('缺少微信appid')
    }
    
    if (typeof config.wechat.enabled !== 'boolean') {
      warnings.push('微信启用状态未明确设置')
    }
    
    if (typeof config.wechat.shareEnabled !== 'boolean') {
      warnings.push('分享启用状态未明确设置')
    }
  }
  
  // 检查功能配置
  if (!config.features) {
    warnings.push('缺少功能配置(features)')
  }
  
  // 检查状态
  if (!config.status || !['active', 'inactive', 'maintenance'].includes(config.status)) {
    warnings.push('项目状态未设置或无效，默认视为inactive')
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    totalErrors: errors.length,
    totalWarnings: warnings.length
  }
}

/**
 * 验证所有项目配置的完整性
 * @returns {array} 所有项目配置的验证结果
 */
export const validateAllProjectConfigs = () => {
  return projectConfigs.map(config => ({
    projectId: config.projectId,
    projectName: config.projectName,
    ...validateProjectConfig(config)
  }))
}

// 重新导出配置数据和错误码定义
export { projectConfigs, ProjectConfigError }